import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { scheduleKeys } from "../query-keys";
import type { EventId, ScheduleSlotId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { scheduleKeys } from "../query-keys";

export type ScheduleBoard = components["schemas"]["FleetScheduleBoard"];
export type ScheduleTeamEntry = components["schemas"]["FleetScheduleTeamEntry-Output"];
export type ScheduleSlot = components["schemas"]["ScheduleSlotResponse"];
export type ScheduleStatus = components["schemas"]["ScheduleSlotStatus"];
export type ScheduleTaskType = components["schemas"]["ScheduleTaskType"];
export type ScheduleSignoff = components["schemas"]["ScheduleSignoff"];
type ScheduleSlotCreate = components["schemas"]["ScheduleSlotCreate"];
type ScheduleSlotUpdate = components["schemas"]["ScheduleSlotUpdate"];

// Merge an update into the matching slot of a cached board, applying only the
// fields that were actually sent (so a move doesn't wipe duration, etc.).
function patchSlot(
  board: ScheduleBoard,
  slotId: ScheduleSlotId,
  data: ScheduleSlotUpdate,
): ScheduleBoard {
  return {
    ...board,
    teams: board.teams.map((team) => ({
      ...team,
      slots: team.slots.map((s) =>
        s.id === slotId
          ? {
              ...s,
              ...(data.scheduled_start !== undefined
                ? { scheduled_start: data.scheduled_start }
                : {}),
              ...(data.duration_minutes !== undefined
                ? { duration_minutes: data.duration_minutes }
                : {}),
              ...(data.task_type != null ? { task_type: data.task_type } : {}),
            }
          : s,
      ),
    })),
  };
}

export function useScheduleBoard(eventId: EventId) {
  return useQuery({
    queryKey: scheduleKeys.byEvent(eventId),
    queryFn: async () => {
      const result = await api.GET("/api/events/{event_id}/schedule", {
        params: { path: { event_id: eventId } },
      });
      return throwIfError(result, "Failed to fetch schedule");
    },
    enabled: !!eventId,
  });
}

export function useCreateScheduleSlot(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ScheduleSlotCreate) => {
      const result = await api.POST("/api/events/{event_id}/schedule/slots", {
        params: { path: { event_id: eventId } },
        body: data,
      });
      return throwIfError(result, "Failed to create schedule slot");
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(eventId) }),
  });
}

export function useUpdateScheduleSlot(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slotId,
      data,
    }: {
      slotId: ScheduleSlotId;
      data: ScheduleSlotUpdate;
    }) => {
      const result = await api.PATCH(
        "/api/events/{event_id}/schedule/slots/{slot_id}",
        { params: { path: { event_id: eventId, slot_id: slotId } }, body: data },
      );
      return throwIfError(result, "Failed to update schedule slot");
    },
    // Optimistic: patch the cached board so a moved/resized block stays where
    // it was dropped instead of snapping back until the refetch lands.
    onMutate: async ({ slotId, data }) => {
      const key = scheduleKeys.byEvent(eventId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ScheduleBoard>(key);
      if (previous) qc.setQueryData<ScheduleBoard>(key, patchSlot(previous, slotId, data));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(scheduleKeys.byEvent(eventId), context.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(eventId) }),
  });
}

/** Freeze/unfreeze the plan. Reversible, unlike release. */
export function useSetScheduleLock(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (locked: boolean) => {
      const path = { params: { path: { event_id: eventId } } };
      const result = locked
        ? await api.POST("/api/events/{event_id}/schedule/lock", path)
        : await api.DELETE("/api/events/{event_id}/schedule/lock", path);
      return throwIfError(result, locked ? "Failed to freeze the schedule" : "Failed to unfreeze the schedule");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(eventId) }),
  });
}

export function useSetScheduleRelease(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (released: boolean) => {
      const path = { params: { path: { event_id: eventId } } };
      const result = released
        ? await api.POST("/api/events/{event_id}/schedule/release", path)
        : await api.DELETE("/api/events/{event_id}/schedule/release", path);
      return throwIfError(
        result,
        released ? "Failed to release the schedule" : "Failed to withdraw the schedule",
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(eventId) }),
  });
}

export function useDeleteScheduleSlot(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slotId: ScheduleSlotId) => {
      const result = await api.DELETE(
        "/api/events/{event_id}/schedule/slots/{slot_id}",
        { params: { path: { event_id: eventId, slot_id: slotId } } },
      );
      return throwIfError(result, "Failed to delete schedule slot");
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(eventId) }),
  });
}
